import type { ApplicationInsights as ApplicationInsightsClient } from '@microsoft/applicationinsights-web';

type TelemetryProperties = Record<string, string | number | boolean | null | undefined>;

interface TelemetryConfigResponse {
  connectionString?: string;
}

interface QueuedEvent {
  name: string;
  properties: Record<string, string | number | boolean>;
}

let telemetryClient: ApplicationInsightsClient | null = null;
let initializationPromise: Promise<void> | null = null;
let pendingAuthenticatedUserId: string | null = null;
const queuedEvents: QueuedEvent[] = [];

function safePropertyValue(value: unknown): string | number | boolean | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().replace(/[\r\n\t]+/g, ' ');
  return normalized ? normalized.slice(0, 160) : undefined;
}

function safeProperties(properties: TelemetryProperties): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries(properties)
      .map(([key, value]) => [key, safePropertyValue(value)] as const)
      .filter((entry): entry is readonly [string, string | number | boolean] => entry[1] !== undefined),
  );
}

export function normalizeTelemetryPath(pathname: string): string {
  const path = pathname.split(/[?#]/, 1)[0] || '/';
  const segments = path.split('/').map((segment) => {
    if (!segment) return segment;
    if (/^[A-Z]{2,8}_[a-zA-Z0-9]{8,}$/i.test(segment)) return ':record';
    if (/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(segment)) return ':id';
    if (/^\d{6,}$/.test(segment)) return ':id';
    if (/^[a-zA-Z0-9_-]{48,}$/.test(segment)) return ':token';
    return segment;
  });
  return segments.join('/') || '/';
}

export function sanitizeTelemetryUrl(value: string, baseUrl = 'https://portal.linkhomecare.com'): string {
  try {
    const parsed = new URL(value, baseUrl);
    parsed.search = '';
    parsed.hash = '';
    parsed.pathname = normalizeTelemetryPath(parsed.pathname);
    return parsed.toString();
  } catch {
    return value.split(/[?#]/, 1)[0].slice(0, 300);
  }
}

function sanitizeOperationName(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const withoutQuery = value.split(/[?#]/, 1)[0];
  const requestMatch = withoutQuery.match(/^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)\s+(.+)$/i);
  if (requestMatch) {
    const requestPath = sanitizeTelemetryUrl(requestMatch[2], window.location.origin);
    return `${requestMatch[1].toUpperCase()} ${new URL(requestPath).pathname}`.slice(0, 300);
  }
  return normalizeTelemetryPath(withoutQuery).slice(0, 300);
}

function sanitizePotentialUrl(value: string): string {
  if (/^https?:\/\//i.test(value) || value.startsWith('/')) {
    return sanitizeTelemetryUrl(value, window.location.origin);
  }
  return value.split(/[?#]/, 1)[0].slice(0, 300);
}

function sanitizeTelemetryItem(item: unknown): boolean {
  if (!item || typeof item !== 'object') return true;
  const envelope = item as { baseData?: Record<string, unknown> };
  const baseData = envelope.baseData;
  if (!baseData) return true;

  for (const key of ['uri', 'refUri', 'data', 'target']) {
    if (typeof baseData[key] === 'string') {
      baseData[key] = sanitizePotentialUrl(baseData[key] as string);
    }
  }
  const operationName = sanitizeOperationName(baseData.name);
  if (operationName) baseData.name = operationName;
  return true;
}

function acquisitionProperties(): TelemetryProperties {
  const query = new URLSearchParams(window.location.search);
  let referrerHost = '';
  try {
    referrerHost = document.referrer ? new URL(document.referrer).hostname : '';
  } catch {
    referrerHost = '';
  }

  return {
    entryPath: normalizeTelemetryPath(window.location.pathname),
    referrerHost,
    utmSource: query.get('utm_source'),
    utmMedium: query.get('utm_medium'),
    utmCampaign: query.get('utm_campaign'),
  };
}

function flushQueuedEvents(): void {
  if (!telemetryClient) return;
  for (const event of queuedEvents.splice(0)) {
    telemetryClient.trackEvent({ name: event.name }, event.properties);
  }
}

async function loadTelemetry(): Promise<void> {
  const abortController = new AbortController();
  const timeout = window.setTimeout(() => abortController.abort(), 3000);
  try {
    const response = await fetch('/api/telemetry-config', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      signal: abortController.signal,
    });
    if (!response.ok) return;
    const config = await response.json() as TelemetryConfigResponse;
    if (!config.connectionString) return;

    const { ApplicationInsights } = await import('@microsoft/applicationinsights-web');
    const client = new ApplicationInsights({
      config: {
        connectionString: config.connectionString,
        enableAutoRouteTracking: true,
        autoTrackPageVisitTime: true,
        enableUnhandledPromiseRejectionTracking: true,
        enableCorsCorrelation: true,
        disableAjaxTracking: false,
        disableFetchTracking: false,
        excludeRequestFromAutoTrackingPatterns: [/\/api\/telemetry-config(?:\?|$)/],
      },
    });
    client.addTelemetryInitializer(sanitizeTelemetryItem);
    client.loadAppInsights();
    telemetryClient = client;

    if (pendingAuthenticatedUserId) {
      client.setAuthenticatedUserContext(pendingAuthenticatedUserId, undefined, true);
    }
    client.trackPageView({
      name: normalizeTelemetryPath(window.location.pathname),
      uri: sanitizeTelemetryUrl(window.location.href, window.location.origin),
    });
    client.trackEvent({ name: 'portal_session_started' }, safeProperties(acquisitionProperties()));
    flushQueuedEvents();
  } catch (error) {
    if (!(error instanceof DOMException && error.name === 'AbortError')) {
      console.warn('[Telemetry] Browser monitoring could not be initialized');
    }
  } finally {
    window.clearTimeout(timeout);
  }
}

export function initializeTelemetry(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  initializationPromise ??= loadTelemetry();
  return initializationPromise;
}

export function trackProductEvent(name: string, properties: TelemetryProperties = {}): void {
  const event = { name, properties: safeProperties(properties) };
  if (telemetryClient) {
    telemetryClient.trackEvent({ name: event.name }, event.properties);
    return;
  }
  if (queuedEvents.length < 100) queuedEvents.push(event);
}

export function setTelemetryUser(userId: number | string | null | undefined): void {
  pendingAuthenticatedUserId = userId === null || userId === undefined ? null : String(userId);
  if (!telemetryClient) return;
  if (pendingAuthenticatedUserId) {
    telemetryClient.setAuthenticatedUserContext(pendingAuthenticatedUserId, undefined, true);
  } else {
    telemetryClient.clearAuthenticatedUserContext();
  }
}
